import './VerifyEmail.scss';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Button } from '../../components/Button/Button';
import { Title } from '../../components/Title/Title';
import { backendURL } from '../../App';
import {
  clearPendingVerify,
  getPendingVerify,
  setPendingVerify,
} from '../../helpers/pendingVerify';
import { formVariants } from '../../animationsVariants/FormVariants';
import { homeForUser, homeForUserType, userTypeOf } from '../../helpers/userRouting';
import { motion } from 'framer-motion';
import { queryCache } from 'react-query';
import axios from 'axios';
import consoleLog from '../../helpers/consoleLog';

const RESEND_COOLDOWN = 60;
const SUPPORT_EMAIL = 'support@iidf-accel.ru';

type PageState = 'loading' | 'pending' | 'expired' | 'error';

export const VerifyEmail: React.FC<{}> = () => {
  const history = useHistory();

  const [state, setState] = useState<PageState>('loading');
  const [email, setEmail] = useState('');
  const [resendToken, setResendToken] = useState(getPendingVerify());
  const [countdown, setCountdown] = useState(0);
  const [resending, setResending] = useState(false);
  const [resentNotice, setResentNotice] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  const startCountdown = useCallback((seconds: number = RESEND_COOLDOWN) => {
    setCountdown(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const autoLoginAndRedirect = useCallback((data: any) => {
    localStorage.setItem('skillsSurveyUserToken', data.token);
    localStorage.setItem('skillsSurveyUserID', data.user.id);
    localStorage.setItem('skillsSurveyUserType', userTypeOf(data.user));
    clearPendingVerify();
    // Hard redirect: AnimatePresence exitBeforeEnter может зависнуть
    // на exit-анимации, оставляя старый pages поверх нового.
    window.location.href = homeForUser(data.user);
  }, []);

  const pollCheckVerification = useCallback((token: string, onPending?: () => void) => {
    axios
      .post(`${backendURL}auth/check-verification/`, { resend_token: token })
      .then(r => {
        if (r.data.verified) {
          autoLoginAndRedirect(r.data);
          return;
        }
        setEmail(r.data.email);
        setState(r.data.expired ? 'expired' : 'pending');
        if (onPending) onPending();
      })
      .catch(err => {
        if (err?.response?.status === 400 || err?.response?.status === 404) {
          clearPendingVerify();
          history.replace('/');
        } else {
          consoleLog('error', 'check-verification', err);
          setState('error');
        }
      });
  }, [autoLoginAndRedirect, history]);

  useEffect(() => {
    // Бесполезно вызывать check-verification если юзер уже залогинен —
    // избегаем создание лишнего Knox-token.
    const userToken = localStorage.getItem('skillsSurveyUserToken');
    if (userToken) {
      const userType = localStorage.getItem('skillsSurveyUserType');
      history.replace(homeForUserType(userType));
      return;
    }
    if (!resendToken) {
      history.replace('/');
      return;
    }
    pollCheckVerification(resendToken);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onResend = () => {
    if (!resendToken || countdown > 0 || resending) return;
    setResending(true);
    setResentNotice(false);
    axios
      .post(`${backendURL}auth/resend-verification/`, { resend_token: resendToken })
      .then(result => {
        setResending(false);
        setResentNotice(true);
        if (result.data.resend_token) {
          setResendToken(result.data.resend_token);
          setPendingVerify(result.data.resend_token);
        }
        startCountdown();
        setState('pending');
      })
      .catch(error => {
        setResending(false);
        const retryAfter = error?.response?.data?.retry_after;
        startCountdown(typeof retryAfter === 'number' ? retryAfter : RESEND_COOLDOWN);
      });
  };

  const onIConfirmed = () => {
    if (!resendToken) return;
    setConfirmError('');
    pollCheckVerification(resendToken, () => {
      setConfirmError('Email ещё не подтверждён');
    });
  };

  const onLogout = () => {
    clearPendingVerify();
    history.push('/');
  };

  return (
    <main className='verifyEmail'>
      <motion.div
        className='verifyEmailForm formContainer'
        variants={formVariants}
        initial='initial'
        animate='animate'
        exit='exit'
      >
        <div className='verifyEmailContent'>
          {state === 'loading' && null}

          {state === 'error' && (
            <>
              <Title>Ошибка</Title>
              <div className='text'>Не удалось загрузить страницу. Обновите её.</div>
            </>
          )}

          {(state === 'pending' || state === 'expired') && (
            <>
              <Title>Подтвердите email</Title>

              {state === 'expired' && (
                <div className='expiredBanner'>
                  Ссылка устарела или недействительна. Отправьте новое письмо.
                </div>
              )}

              <div className='text'>
                Мы отправили письмо на <strong>{email}</strong>. Перейдите по ссылке из письма, чтобы активировать аккаунт.
              </div>

              <ul className='hints'>
                <li>Проверьте, что email указан правильно — <strong>{email}</strong></li>
                <li>Не пришло письмо? Проверьте папку «Спам»</li>
              </ul>

              {resentNotice && <div className='resendSuccess'>Письмо отправлено повторно</div>}

              <div className='actions'>
                <Button
                  onClick={onResend}
                  type='button'
                  width='100%'
                  size='small'
                  color='blue'
                  text='Отправить письмо повторно'
                  className='verifyEmailButton'
                  disabled={countdown > 0 || resending}
                  loading={resending}
                />
                {countdown > 0 && (
                  <div className='resendCountdown'>Повторная отправка через {countdown} сек.</div>
                )}

                <Button
                  onClick={onIConfirmed}
                  type='button'
                  width='100%'
                  size='small'
                  color='black'
                  text='Я подтвердил, продолжить'
                  className='verifyEmailButton'
                />
                {confirmError && <div className='confirmError'>{confirmError}</div>}

                <button onClick={onLogout} className='logoutLink' data-cy='verifyEmailLogout'>
                  Выйти
                </button>
              </div>

              <div className='supportNote'>
                Возникли проблемы? Напишите на{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </main>
  );
};
